/**
 * SearchIndexService
 *
 * Shared search helpers used by indexing and query paths.
 */

const NON_ALNUM_REGEX = /[^a-z0-9]+/g;

export const DEFAULT_INDEX_PREFIX_MAX = 24;
export const DEFAULT_INDEX_TOKEN_MAX = 64;
export const DEFAULT_INDEX_ENTRY_LIMIT = 1200;

/**
 * Normalizes free text for display/query usage.
 */
export function normalizeSearchText(value) {
  return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Normalizes a token to alpha-numeric lowercase.
 */
export function normalizeSearchToken(value) {
  return normalizeSearchText(value).replace(NON_ALNUM_REGEX, '');
}

/**
 * Tokenizes free text query into unique normalized tokens.
 */
export function tokenizeSearchQuery(value) {
  const raw = normalizeSearchText(value);
  if (!raw) return [];

  const tokens = raw
    .split(/\s+/)
    .map((part) => normalizeSearchToken(part))
    .filter(Boolean);

  return Array.from(new Set(tokens));
}

/**
 * Builds token prefixes for `array-contains` style lookup.
 */
export function buildTokenPrefixes(token, maxPrefixLength = DEFAULT_INDEX_PREFIX_MAX) {
  const normalized = normalizeSearchToken(token);
  if (!normalized) return [];

  const capped = normalized.slice(0, Math.max(1, Number(maxPrefixLength) || DEFAULT_INDEX_PREFIX_MAX));
  const prefixes = [];
  for (let i = 1; i <= capped.length; i += 1) {
    prefixes.push(capped.slice(0, i));
  }
  return prefixes;
}

/**
 * Builds searchable index entries from free-text inputs.
 */
export function buildSearchIndexFromTexts(
  texts = [],
  {
    maxPrefixLength = DEFAULT_INDEX_PREFIX_MAX,
    maxTokenLength = DEFAULT_INDEX_TOKEN_MAX,
    maxEntries = DEFAULT_INDEX_ENTRY_LIMIT,
  } = {}
) {
  const out = new Set();
  const prefixMax = Math.max(1, Number(maxPrefixLength) || DEFAULT_INDEX_PREFIX_MAX);
  const tokenMax = Math.max(8, Number(maxTokenLength) || DEFAULT_INDEX_TOKEN_MAX);
  const entryMax = Math.max(64, Number(maxEntries) || DEFAULT_INDEX_ENTRY_LIMIT);

  const append = (value) => {
    if (value == null) return;

    if (Array.isArray(value)) {
      value.forEach(append);
      return;
    }

    const normalized = normalizeSearchText(value);
    if (!normalized) return;

    const tokens = normalized
      .split(/\s+/)
      .map((part) => normalizeSearchToken(part))
      .filter(Boolean)
      .map((token) => token.slice(0, tokenMax));

    for (const token of tokens) {
      const prefixes = buildTokenPrefixes(token, prefixMax);
      for (const prefix of prefixes) {
        out.add(prefix);
        if (out.size >= entryMax) return;
      }
      if (out.size >= entryMax) return;
    }
  };

  append(texts);
  return Array.from(out);
}

/**
 * Returns true when all tokens exist in a node index.
 */
export function indexMatchesAllTokens(index, tokens) {
  const normalizedTokens = Array.isArray(tokens)
    ? tokens.map((token) => normalizeSearchToken(token)).filter(Boolean)
    : [];

  if (!normalizedTokens.length) return true;

  const indexSet = new Set(Array.isArray(index) ? index : []);
  return normalizedTokens.every((token) => indexSet.has(token));
}